import React, { useState, useEffect } from 'react';
import { Send, Save, Copy, X, Mail, FileText, Paperclip, AlertCircle, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import recommendationLetterService from '../../services/recommendationLetterService';
import { useFamilyContext } from '../../contexts/FamilyContext';

const FollowUpEmailComposer = ({ 
  recommendation, 
  isOpen, 
  onClose,
  onSave,
  onSend 
}) => {
  const { currentUser } = useFamilyContext();
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Form fields for follow-up email
  const [formData, setFormData] = useState({
    attachments: {
      bragSheet: false,
      personalStatement: false,
      collegeList: false,
      specificForms: false,
      transcript: false,
      resume: false
    },
    context: {
      intendedMajor: '',
      whyMajor: '',
      specificContribution: '',
      uniqueObservation: '',
      additionalInfo: ''
    },
    deadlines: {
      earliestDeadline: '',
      submissionMethod: 'Common App',
      specialInstructions: ''
    }
  });

  useEffect(() => {
    if (isOpen && recommendation) {
      // Pre-fill with any saved data if available
      const savedData = recommendation.follow_up_data;
      if (savedData) {
        setFormData(savedData);
      }
    }
  }, [isOpen, recommendation]);

  const updateField = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const generateEmail = () => {
    const studentName = currentUser?.name || 'Student';
    const attachmentsList = Object.entries(formData.attachments)
      .filter(([_, checked]) => checked)
      .map(([key, _]) => {
        const labels = {
          bragSheet: 'Brag sheet/activity resume',
          personalStatement: 'Personal statement draft',
          collegeList: 'College list with deadlines',
          specificForms: 'Specific forms and submission instructions',
          transcript: 'Unofficial transcript',
          resume: 'Resume'
        };
        return `- ${labels[key]}`;
      })
      .join('\n');

    const subject = `Materials for Letter of Recommendation - ${studentName}`;
    
    let body = `Dear ${recommendation.recommender_name || '[Teacher Name]'},

Thank you so much for agreeing to write my letter of recommendation! I truly appreciate your support in my college application process.

As we discussed, I've attached the following materials:
${attachmentsList || '- [Please select attachments above]'}`;

    if (formData.context.intendedMajor) {
      body += `

**My Academic Goals:**
I'm planning to study ${formData.context.intendedMajor}${formData.context.whyMajor ? ` because ${formData.context.whyMajor}` : ''}.`;
    }

    if (formData.context.specificContribution) {
      body += `

**How I've Contributed to Your Class:**
${formData.context.specificContribution}`;
    }

    if (formData.context.uniqueObservation) {
      body += `

**Something You've Observed That Others Might Not Know:**
${formData.context.uniqueObservation}`;
    }

    if (formData.deadlines.earliestDeadline) {
      body += `

**Deadline Information:**
- Earliest deadline: ${formData.deadlines.earliestDeadline}
- Submission method: ${formData.deadlines.submissionMethod}${formData.deadlines.specialInstructions ? `
- Special instructions: ${formData.deadlines.specialInstructions}` : ''}`;
    }

    if (formData.context.additionalInfo) {
      body += `

**Additional Information:**
${formData.context.additionalInfo}`;
    }

    body += `

Please let me know if you need any additional information or materials. I'm happy to provide whatever would be helpful for you.

Thank you again for your time and support!

Best regards,
${studentName}`;

    return { subject, body };
  };

  const handleSaveDraft = async () => {
    try {
      if (!recommendation?.id) {
        toast.error('Unable to save - no recommendation selected');
        return;
      }
      
      setSaving(true);
      const { subject, body } = generateEmail();
      
      await recommendationLetterService.saveEmailContent(recommendation.id, {
        type: 'follow_up',
        content: body,
        status: 'draft'
      });
      
      // Also save the form data for future editing
      await recommendationLetterService.updateRecommendation(recommendation.id, {
        follow_up_data: formData
      });
      
      toast.success('Follow-up email draft saved!');
      if (onSave) {
        onSave(body);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAsSent = async () => {
    try {
      if (!recommendation?.id) {
        toast.error('Unable to save - no recommendation selected');
        return;
      }
      
      setSending(true);
      const { subject, body } = generateEmail();
      const fullEmail = `Subject: ${subject}\n\n${body}`;
      
      await recommendationLetterService.saveEmailContent(recommendation.id, {
        type: 'follow_up',
        content: body,
        status: 'sent'
      });
      
      // Save form data
      await recommendationLetterService.updateRecommendation(recommendation.id, {
        follow_up_data: formData
      });
      
      // Copy to clipboard
      await navigator.clipboard.writeText(fullEmail);
      
      toast.success('Follow-up email marked as sent and copied to clipboard!');
      if (onSend) {
        onSend(body);
      }
      onClose();
    } catch (error) {
      console.error('Error marking as sent:', error);
      toast.error('Failed to mark as sent');
    } finally {
      setSending(false);
    }
  };

  const handleCopyEmail = async () => {
    try {
      const { subject, body } = generateEmail();
      const fullEmail = `Subject: ${subject}\n\n${body}`;
      await navigator.clipboard.writeText(fullEmail);
      toast.success('Email copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-slate-600" />
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Follow-up Email with Materials</h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  To: {recommendation?.recommender_name} • {recommendation?.recommender_email}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Content - Split View */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left: Form */}
          <div className="w-1/2 border-r border-slate-200 overflow-y-auto p-6">
            <div className="space-y-6">
              {/* Attachments Section */}
              <div>
                <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Materials to Attach
                </h3>
                <div className="space-y-2">
                  {Object.entries(formData.attachments).map(([key, checked]) => {
                    const labels = {
                      bragSheet: 'Brag Sheet / Activity Resume',
                      personalStatement: 'Personal Statement Draft',
                      collegeList: 'College List with Deadlines',
                      specificForms: 'Specific Forms',
                      transcript: 'Unofficial Transcript',
                      resume: 'Resume'
                    };
                    return (
                      <label key={key} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => updateField('attachments', key, e.target.checked)}
                          className="rounded border-slate-300 text-azure-600 focus:ring-azure-500"
                        />
                        <span className="text-sm text-slate-700">{labels[key]}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Context Section */}
              <div>
                <h3 className="font-medium text-slate-900 mb-3">Context & Reminders</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Intended Major
                    </label>
                    <input
                      type="text"
                      value={formData.context.intendedMajor}
                      onChange={(e) => updateField('context', 'intendedMajor', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                      placeholder="e.g., Computer Science, Biology, Economics"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Why This Major? (Connection to their class)
                    </label>
                    <textarea
                      value={formData.context.whyMajor}
                      onChange={(e) => updateField('context', 'whyMajor', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
                      rows={2}
                      placeholder="e.g., your chemistry class sparked my interest in research..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Specific Contribution to Their Class
                    </label>
                    <textarea
                      value={formData.context.specificContribution}
                      onChange={(e) => updateField('context', 'specificContribution', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
                      rows={2}
                      placeholder="e.g., Led our group project on renewable energy..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Unique Quality They've Observed
                    </label>
                    <textarea
                      value={formData.context.uniqueObservation}
                      onChange={(e) => updateField('context', 'uniqueObservation', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
                      rows={2}
                      placeholder="e.g., How I helped struggling classmates understand concepts..."
                    />
                  </div>
                </div>
              </div>

              {/* Deadline Section */}
              <div>
                <h3 className="font-medium text-slate-900 mb-3">Deadline Information</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Earliest Deadline
                    </label>
                    <input
                      type="date"
                      value={formData.deadlines.earliestDeadline}
                      onChange={(e) => updateField('deadlines', 'earliestDeadline', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Submission Method
                    </label>
                    <select
                      value={formData.deadlines.submissionMethod}
                      onChange={(e) => updateField('deadlines', 'submissionMethod', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                    >
                      <option value="Common App">Common App</option>
                      <option value="Coalition App">Coalition App</option>
                      <option value="School Portal">School Portal</option>
                      <option value="Email">Direct Email</option>
                      <option value="Naviance">Naviance</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Special Instructions
                    </label>
                    <input
                      type="text"
                      value={formData.deadlines.specialInstructions}
                      onChange={(e) => updateField('deadlines', 'specialInstructions', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                      placeholder="e.g., Please upload to specific portal"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Additional Information
                </label>
                <textarea
                  value={formData.context.additionalInfo}
                  onChange={(e) => updateField('context', 'additionalInfo', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
                  rows={3}
                  placeholder="Any other helpful context..."
                />
              </div>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="w-1/2 overflow-y-auto p-6 bg-slate-50">
            <h3 className="font-medium text-slate-900 mb-3">Email Preview</h3>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <div className="mb-3 pb-3 border-b border-slate-200">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Subject:</span> {generateEmail().subject}
                </p>
              </div>
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                {generateEmail().body}
              </pre>
            </div>

            {/* Tips */}
            <div className="mt-4 bg-azure-50 border border-azure-200 rounded-lg p-3">
              <div className="flex gap-2">
                <AlertCircle className="w-4 h-4 text-azure-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-azure-800">
                  <p className="font-medium mb-1">Before Sending:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-azure-700">
                    <li>Attach all mentioned documents</li>
                    <li>Proofread your personal details</li>
                    <li>Verify the deadline date</li>
                    <li>Include any school-specific requirements</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                className="px-3 sm:px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={handleCopyEmail}
                className="px-3 sm:px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy to Clipboard
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-3 sm:px-4 py-2 text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAsSent}
                disabled={sending}
                className="px-3 sm:px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {sending ? 'Marking as Sent...' : 'Mark as Sent & Copy'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FollowUpEmailComposer;